import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import useUser, { isUserDoctor, isUserClient } from "../../hooks/User";
import { ROUTES } from "../../routes";
import NotificationBadge from "../doctor/NotificationBadge";

import "./header.css";

// User icon component
function UserIcon({ className = "" }) {
    return (
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className={className}
        >
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
        </svg>
    );
}

// Logo icon
function LogoIcon() {
    return (
        <svg 
            version="1.0" 
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 294 295"
            preserveAspectRatio="xMidYMid slice"
            fill="currentColor"
        >
            <g transform="translate(0.000000,295.000000) scale(0.100000,-0.100000)"
               fill="#2f6fed"
               stroke="none">
                <path d="M1281 2855 c-215 -47 -403 -162 -535 -327 -294 -367 -266 -893 63
-1227 166 -168 402 -271 620 -271 l65 0 -39 -41 c-213 -220 -572 -258 -836
-87 -191 122 -309 340 -309 568 0 41 5 95 11 120 8 35 6 63 -7 130 -11 54 -18
135 -18 222 -1 131 -2 136 -19 120 -31 -27 -105 -145 -137 -217 -60 -133 -75
-213 -74 -385 1 -138 4 -165 27 -243 48 -155 117 -276 221 -385 333 -349 866
-385 1246 -85 196 155 314 377 344 648 l11 100 58 -70 c202 -244 221 -557 50
-817 -62 -94 -150 -171 -258 -224 -138 -68 -282 -90 -418 -63 -46 10 -70 8
-144 -7 -58 -12 -134 -18 -220 -18 -73 -1 -133 -2 -133 -3 0 -11 146 -106 215
-139 145 -71 223 -87 405 -87 140 0 164 2 243 26 167 51 303 134 418 253 110
116 177 232 225 389 25 82 27 104 27 245 0 182 -16 260 -87 405 -95 198 -248
347 -450 441 -92 42 -176 63 -306 78 l-105 12 70 57 c145 120 301 173 480 164
178 -10 304 -66 436 -197 149 -147 208 -302 195 -514 -4 -61 -8 -122 -8 -136
-1 -14 7 -71 16 -128 12 -67 17 -138 14 -206 -4 -122 -4 -122 77 -2 65 96 117
221 141 336 24 122 16 314 -20 433 -100 335 -365 578 -706 649 -131 28 -307
21 -433 -16 -384 -113 -651 -448 -669 -838 -2 -38 -5 -68 -8 -68 -13 0 -90 89
-122 140 -234 373 -62 852 359 1001 72 26 269 37 337 20 36 -9 58 -8 113 6 47
11 116 16 220 17 84 1 153 3 153 6 0 9 -110 85 -177 122 -72 40 -182 79 -271
97 -73 15 -274 12 -351 -4z m290 -1230 c64 -42 99 -132 79 -204 -50 -187 -317
-189 -365 -3 -24 92 13 174 98 217 64 33 130 29 188 -10z"/>
            </g>
        </svg>
    );
}

// Navigation link with active state
function NavLink({ to, children, onClick, exact = false }) {
    const location = useLocation();
    let isActive;
    
    if (exact) {
        // Exact match only (for routes that are prefixes of other routes)
        isActive = location.pathname === to;
    } else {
        // Match exact path or paths that start with this path + '/'
        isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    }
    
    return (
        <Link 
            to={to} 
            className={`nav-link ${isActive ? 'active' : ''}`}
            onClick={onClick}
            viewTransition
        >
            {children}
        </Link>
    );
}

// Desktop Navigation
function DesktopNav({ isDoctor, isClient, onProfileClick, isProfileActive }) {
    return (
        <nav className="header-nav-desktop">
            <div className="header-nav-links">
                {isDoctor ? (
                    <>
                        <NavLink to={ROUTES.SURVEYS} exact>Создать опрос</NavLink>
                        {isClient && (
                            <NavLink to={ROUTES.MY_SURVEYS}>Назначенные опросы</NavLink>
                        )}
                    </>
                ) : (
                    <NavLink to={ROUTES.MY_SURVEYS}>Назначенные опросы</NavLink>
                )}
                {isDoctor && <NavLink to={ROUTES.QUESTIONS}>Вопросы</NavLink>}
                {isDoctor && (
                    <NavLink to={ROUTES.NOTIFICATIONS}>
                        Уведомления
                        <NotificationBadge />
                    </NavLink>
                )}
                {isDoctor && <NavLink to={ROUTES.PATIENTS}>Пациенты</NavLink>}
                {isDoctor && <NavLink to={ROUTES.PATIENT_GROUPS}>Группы</NavLink>}
                {isClient && <NavLink to={ROUTES.DOCTORS}>Врачи</NavLink>}
                {isClient && <NavLink to={ROUTES.MY_MEDICATIONS}>Лекарства</NavLink>}
            </div>
            <button 
                className={`header-profile-btn ${isProfileActive ? 'active' : ''}`}
                onClick={onProfileClick}
                aria-label="Профиль"
            >
                <UserIcon />
                <span className="header-profile-text">Профиль</span>
            </button>
        </nav>
    );
}

// Mobile Navigation
function MobileNav({ isDoctor, isClient, isOpen, setIsOpen, onProfileClick, isProfileActive }) {
    return (
        <>
            <div className="header-mobile-controls">
                <button 
                    className={`header-profile-btn mobile ${isProfileActive ? 'active' : ''}`}
                    onClick={onProfileClick}
                    aria-label="Профиль"
                >
                    <UserIcon />
                </button>
                <button 
                    className={`header-hamburger ${isOpen ? 'open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? "Закрыть меню" : "Открыть меню"}
                    aria-expanded={isOpen}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>
            
            <div className={`header-mobile-menu ${isOpen ? 'open' : ''}`}>
                {isDoctor ? (
                    <>
                        <NavLink to={ROUTES.SURVEYS} exact onClick={() => setIsOpen(false)}>Создать опрос</NavLink>
                        {isClient && (
                            <NavLink to={ROUTES.MY_SURVEYS} onClick={() => setIsOpen(false)}>Назначенные опросы</NavLink>
                        )}
                    </>
                ) : (
                    <NavLink to={ROUTES.MY_SURVEYS} onClick={() => setIsOpen(false)}>Назначенные опросы</NavLink>
                )}
                {isDoctor && <NavLink to={ROUTES.QUESTIONS} onClick={() => setIsOpen(false)}>Вопросы</NavLink>}
                {isDoctor && (
                    <NavLink to={ROUTES.NOTIFICATIONS} onClick={() => setIsOpen(false)}>
                        Уведомления
                        <NotificationBadge />
                    </NavLink>
                )}
                {isDoctor && <NavLink to={ROUTES.PATIENTS} onClick={() => setIsOpen(false)}>Пациенты</NavLink>}
                {isDoctor && <NavLink to={ROUTES.PATIENT_GROUPS} onClick={() => setIsOpen(false)}>Группы</NavLink>}
                {isClient && <NavLink to={ROUTES.DOCTORS} onClick={() => setIsOpen(false)}>Врачи</NavLink>}
                {isClient && <NavLink to={ROUTES.MY_MEDICATIONS} onClick={() => setIsOpen(false)}>Лекарства</NavLink>}
            </div>
            
            {isOpen && (
                <div 
                    className="header-mobile-overlay"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </>
    );
}

// Public Header (for non-authenticated pages)
export function PublicHeader() {
    const location = useLocation();
    const nav = useNavigate();
    const { data: userData } = useUser();
    const isAuthPage = location.pathname === ROUTES.AUTH || location.pathname === ROUTES.AUTH_CODE;
    
    const isAuthenticated = !!(userData && (isUserDoctor(userData) || isUserClient(userData)));

    const handleProfileClick = () => {
        nav(ROUTES.PROFILE, { viewTransition: true });
    };

    // Logo always links to home page
    const getHomeRoute = () => ROUTES.HOME;
    
    return (
        <header className="header public">
            <div className="header-container">
                <Link to={getHomeRoute()} className="header-logo" viewTransition>
                    <div className="header-logo-icon">
                        <LogoIcon />
                    </div>
                    <span className="header-logo-text">Spiria</span>
                </Link>
                
                {!isAuthPage && (
                    <nav className="header-nav-public">
                        {isAuthenticated ? (
                            <button 
                                className="about-header-profile-btn"
                                onClick={handleProfileClick}
                            >
                                <UserIcon className="" />
                                <span>Профиль</span>
                            </button>
                        ) : (
                            <Link to={ROUTES.AUTH} className="header-btn-primary" viewTransition>
                                Войти
                            </Link>
                        )}
                    </nav>
                )}
            </div>
        </header>
    );
}

// Main Header (for authenticated pages)
export default function Header() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { data } = useUser();
    const nav = useNavigate();
    const location = useLocation();
    
    const userIsDoctor = isUserDoctor(data);
    const userIsClient = isUserClient(data);
    
    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);
    
    // Close mobile menu on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);
    
    const handleProfileClick = () => {
        nav(ROUTES.PROFILE, { viewTransition: true });
    };
    
    // Check if profile page is active
    const isProfileActive = location.pathname === ROUTES.PROFILE || location.pathname.startsWith(ROUTES.PROFILE + '/');
    
    return (
        <header className="header">
            <div className="header-container">
                <Link to={ROUTES.HOME} className="header-logo" viewTransition>
                    <div className="header-logo-icon">
                        <LogoIcon />
                    </div>
                    <span className="header-logo-text">Spiria</span>
                </Link>
                
                <div className="header-desktop">
                    <DesktopNav 
                        isDoctor={userIsDoctor}
                        isClient={userIsClient}
                        onProfileClick={handleProfileClick}
                        isProfileActive={isProfileActive}
                    />
                </div>
                
                <div className="header-mobile">
                    <MobileNav 
                        isDoctor={userIsDoctor}
                        isClient={userIsClient}
                        isOpen={isMobileMenuOpen}
                        setIsOpen={setIsMobileMenuOpen}
                        onProfileClick={handleProfileClick}
                        isProfileActive={isProfileActive}
                    />
                </div>
            </div>
        </header>
    );
}
